import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';

interface Props {
    children?: ReactNode;
    onSearch: (value: string) => void;
    setEnableSearch: any;
    hasMRA?: boolean;
    ref: any;
}

export type Ref = HTMLInputElement;

export const SearchLocationInput = forwardRef<Ref, Props>((props, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const typingTimeout = useRef<NodeJS.Timeout | null>(null);

    // Fetch suggestions from Google Places API
    const fetchSuggestions = async (searchText: string) => {
        if (!searchText || searchText.length < 1) {
            setSuggestions([]);
            return;
        }

        let request = {
            input: searchText,
            includedRegionCodes: ['us'],
            includedPrimaryTypes: ['(cities)']
        };

        const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': process.env.NEXT_PUBLIC_GOOGLE_API_KEY || '',
                'X-Goog-FieldMask': 'suggestions.placePrediction.text.text',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            console.error('API call failed:', response.statusText);
            return;
        }

        const { suggestions } = await response.json();
        setSuggestions(suggestions.map((s: any) => s.placePrediction.text.text));
        setShowDropdown(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        if (typingTimeout.current) {
            clearTimeout(typingTimeout.current);
        }
        typingTimeout.current = setTimeout(() => {
            fetchSuggestions(e.target.value);
        }, 100);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                if (prev < suggestions.length - 1) return prev + 1;
                return 0;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                if (prev > 0) return prev - 1;
                return suggestions.length - 1;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            let idx = selectedIndex === -1 ? 0 : selectedIndex;
            if (suggestions[idx]) {
                handleSelect(suggestions[idx]);
            }
        }
    };

    // Handle suggestion select
    const handleSelect = (value: string) => {
        setInputValue(value);
        setShowDropdown(false);
        setSuggestions([]);
        setSelectedIndex(-1);
        props.onSearch(value);
    };

    // Reset selectedIndex when suggestions change
    useEffect(() => {
        setSelectedIndex(-1);
    }, [suggestions]);

    return (
      <div className="w-full min-w-0">
        <label htmlFor="city_state" aria-label="city state" className="hidden">
          City state:
        </label>
        <div className="flex items-center w-full">
          <input
            id="location_search_input"
            aria-label="City state"
            name="city_state"
            ref={ref}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={props?.hasMRA ? 'City, State' : 'City, state, or zip code'}
            type="search"
            autoComplete="off"
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            className="w-full h-6 min-h-0 pl-10 pr-4 border border-black rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition outline-none bg-white text-black placeholder-gray-400 text-base"
            style={{ boxShadow: 'none' }}
          />
        </div>
        <div className="relative w-full min-w-0">
          {showDropdown && suggestions.length > 0 && (
            <ul
              id="location_search_result"
              className="absolute left-0 right-0 w-full min-w-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-1"
            >
              {suggestions.map((item, idx) => (
                <li
                  key={item}
                  className={`flex gap-2 px-4 py-2 cursor-pointer transition-colors ${
                    idx === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-100'
                  }`}
                >
                  <button
                    className={`w-full text-left flex items-center gap-2`}
                    key={item}
                    onMouseDown={() => handleSelect(item)}
                  >
                    <span>
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        className="text-gray-500"
                        style={{ minWidth: 18 }}
                      >
                        <path
                          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span className="truncate text-gray-900">{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
});
